import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCreatePost, useAllTags, useCreateTag } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../hooks/useTranslation';
import { useUpload } from '../lib/uploadContext';
import { preserveNewlines, validateTextWithNewlines, getTextLength } from '../utils/textUtils';
import ErrorToast from '../components/ErrorToast';
import SuccessToast from '../components/SuccessToast';
import VideoTrimEditor from '../components/VideoTrimEditor';
import MultiFileUpload from '../components/MultiFileUpload';
import TextPreview from '../components/TextPreview';
import PollCreator from '../components/PollCreator';
import Layout from '../components/Layout';

const CreatePost: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { data: allTags = [] } = useAllTags({
    retry: false,
    refetchOnWindowFocus: false,
  });
  const createTagMutation = useCreateTag();
  
  const [caption, setCaption] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [selectedCover, setSelectedCover] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [useMultiUpload, setUseMultiUpload] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const tagContainerRef = useRef<HTMLDivElement>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showVideoTrimmer, setShowVideoTrimmer] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [postType, setPostType] = useState<'regular' | 'poll' | 'text'>('text');
  const [pollData, setPollData] = useState<{question: string; options: string[]} | null>(null);
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  
  const createPostMutation = useCreatePost();
  const { startUpload, updateProgress, setError, setSuccess, completeUpload, uploadState } = useUpload();

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Cleanup video preview URL on unmount or when video preview changes
  React.useEffect(() => {
    return () => {
      if (videoPreview && videoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview]);

  // Extract first frame from video as cover image
  const extractVideoFrame = useCallback(async (videoFile: File): Promise<File | null> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.preload = 'metadata';
      video.src = URL.createObjectURL(videoFile);
      
      video.onloadedmetadata = () => {
        video.currentTime = 0.1; // Seek to first frame (0.1s to ensure frame is loaded)
      };
      
      video.onseeked = () => {
        if (ctx) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const coverFile = new File([blob], 'cover.jpg', { type: 'image/jpeg' });
              resolve(coverFile);
            } else {
              resolve(null);
            }
            URL.revokeObjectURL(video.src);
          }, 'image/jpeg', 0.9);
        } else {
          resolve(null);
          URL.revokeObjectURL(video.src);
        }
      };
      
      video.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(video.src);
      };
    });
  }, []);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Clear video if image is selected
      setSelectedVideo(null);
      setVideoPreview(null);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Clear image if video is selected
      setSelectedImage(null);
      setImagePreview(null);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }

      // Check file size (20MB limit)
      const maxSize = 200 * 1024 * 1024; // 20MB in bytes
      if (file.size > maxSize) {
        setShowVideoTrimmer(true);
        setSelectedVideo(file);
        return;
      }

      // Always show trim editor for videos to allow trimming
      setShowVideoTrimmer(true);
      setSelectedVideo(file);
      // Cover will be extracted in handleVideoTrimmerSave after trimming
    }
  };

  const normalizeTagName = (name: string) => name.trim().toLowerCase();

  const handleTagToggle = (tagName: string) => {
    const normalized = normalizeTagName(tagName);
    setSelectedTags(prev =>
      prev.includes(normalized)
        ? prev.filter((t) => t !== normalized)
        : [...prev, normalized]
    );
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedCover(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const addTagByName = useCallback(async (name: string) => {
    const normalized = normalizeTagName(name);
    if (!normalized) return;
    if (selectedTags.includes(normalized)) {
      setTagInput('');
      return;
    }
    try {
      await createTagMutation.mutateAsync(normalized);
      setSelectedTags((prev) => [...prev, normalized]);
      setTagInput('');
      setTagDropdownOpen(false);
    } catch {
      setErrorMessage(t('createPost.createTagError'));
      setShowError(true);
    }
  }, [selectedTags, createTagMutation, t]);

  const selectExistingTag = useCallback((tagName: string) => {
    const normalized = normalizeTagName(tagName);
    if (!normalized || selectedTags.includes(normalized)) {
      setTagInput('');
      setTagDropdownOpen(false);
      return;
    }
    setSelectedTags((prev) => [...prev, normalized]);
    setTagInput('');
    setTagDropdownOpen(false);
  }, [selectedTags]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tagContainerRef.current && !tagContainerRef.current.contains(e.target as Node)) {
        setTagDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const trimmed = tagInput.trim();
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (trimmed) addTagByName(trimmed);
      return;
    }
    if (e.key === 'Backspace' && !trimmed && selectedTags.length > 0) {
      e.preventDefault();
      setSelectedTags((prev) => prev.slice(0, -1));
    }
  };

  const handleVideoTrimmerSave = useCallback(async (editedVideo: File) => {
    setSelectedVideo(editedVideo);
    setShowVideoTrimmer(false);
    // Create object URL for video preview instead of data URL
    const videoUrl = URL.createObjectURL(editedVideo);
    setVideoPreview(videoUrl);
    
    // Auto-extract first frame as cover if no cover is set
    if (!selectedCover) {
      const coverFile = await extractVideoFrame(editedVideo);
      if (coverFile) {
        setSelectedCover(coverFile);
        const reader = new FileReader();
        reader.onload = (e) => {
          setCoverPreview(e.target?.result as string);
        };
        reader.readAsDataURL(coverFile);
      }
    }
  }, [selectedCover, extractVideoFrame]);

  const handleVideoTrimmerCancel = useCallback(() => {
    setShowVideoTrimmer(false);
    setSelectedVideo(null);
    // Clean up video preview URL
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
      setVideoPreview(null);
    }
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  }, [videoPreview]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate caption length only when caption is required (text) or provided
    if (postType === 'text' || caption.trim()) {
      const validation = validateTextWithNewlines(caption, 1000);
      if (!validation.isValid) {
        setErrorMessage(validation.message);
        setShowError(true);
        return;
      }
    }

    // Poll posts require valid question and at least 2 options
    if (postType === 'poll') {
      if (!pollData || !pollData.question?.trim() || (pollData.options?.length ?? 0) < 2) {
        setErrorMessage(t('errors.pollValidation'));
        setShowError(true);
        return;
      }
    }

    // If post type is 'text', don't allow media uploads
    if (postType === 'text' && (selectedImage || selectedVideo || selectedFiles.length > 0)) {
      setErrorMessage(t('errors.textPostNoFile'));
      setShowError(true);
      return;
    }

    // If post type is 'regular' but no media is selected, change to 'text'
    if (postType === 'regular' && !selectedImage && !selectedVideo && selectedFiles.length === 0) {
      // This will be handled in the API call - we'll send postType as 'text' instead
    }

    if (!user) {
      setErrorMessage(t('errors.authFailed'));
      setShowError(true);
      return;
    }

    try {
      // Start global upload tracking
      startUpload(t('createPost.uploadingPost'));

      // Determine final post type: if 'regular' but no media, change to 'text'
      const finalPostType = (postType === 'regular' && !selectedImage && !selectedVideo && selectedFiles.length === 0) 
        ? 'text' 
        : postType;

      // For regular posts with video, extract cover if not set
      let finalCoverFile = selectedCover;
      if (finalPostType === 'regular' && selectedVideo && !selectedCover) {
        const extractedCover = await extractVideoFrame(selectedVideo);
        if (extractedCover) {
          finalCoverFile = extractedCover;
          // Update state for UI preview
          setSelectedCover(extractedCover);
          const reader = new FileReader();
          reader.onload = (e) => {
            setCoverPreview(e.target?.result as string);
          };
          reader.readAsDataURL(extractedCover);
        }
      }

      const postData = {
        caption: finalPostType === 'poll' ? '' : preserveNewlines(caption),
        imageFile: (finalPostType === 'regular' && selectedImage) ? selectedImage : undefined,
        videoFile: (finalPostType === 'regular' && selectedVideo) ? selectedVideo : undefined,
        coverFile: (finalPostType === 'regular' && finalCoverFile) ? finalCoverFile : undefined,
        files: (finalPostType === 'regular' && useMultiUpload) ? selectedFiles : undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        postType: finalPostType,
        pollData: finalPostType === 'poll' ? pollData || undefined : undefined,
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          updateProgress(percentCompleted);
        },
      };

      const result = await createPostMutation.mutateAsync(postData);
      
      // Mark upload as complete
      updateProgress(100);
      completeUpload();
      
      // Notify service worker to refresh cache if app is installed as PWA
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_UPDATE',
          action: 'invalidate',
        });
      }
      
      // Show warnings if any files failed to upload
      if (result.warnings && result.warnings.length > 0) {
        setErrorMessage(t('errors.uploadPartial', { warnings: result.warnings.join(', ') }));
        setShowError(true);
      } else {
        setShowSuccess(true);
      }
      
      // Allow navigation immediately - user can navigate while upload completes
      // The persistent indicator will show the status
      // Auto-navigate after a short delay, but user can navigate manually anytime
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (error: any) {
      // Clear upload state so UI is not stuck in "uploading"
      completeUpload();

      // Update global upload state with error
      let errorMsg = t('createPost.createError');
      if (error.message) {
        if (error.message.includes('فایل خیلی بزرگ است')) {
          errorMsg = t('createPost.fileTooBig');
        } else if (error.message.includes('نوع فایل')) {
          errorMsg = t('createPost.fileTypeNotSupported');
        } else if (error.message.includes('اتصال')) {
          errorMsg = t('createPost.connectionError');
        } else if (error.message.includes('قطع شد') || error.message.includes('بعداً ذخیره شود')) {
          // Timeout/504 message from api interceptor
          errorMsg = error.message;
        } else {
          errorMsg = error.message;
        }
      }

      setError(errorMsg);
      setErrorMessage(errorMsg);
      setShowError(true);
    }
  };

  const clearMedia = () => {
    // Clean up video preview URL
    if (videoPreview && videoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(videoPreview);
    }
    
    setSelectedImage(null);
    setSelectedVideo(null);
    setSelectedCover(null);
    setSelectedFiles([]);
    setImagePreview(null);
    setVideoPreview(null);
    setCoverPreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const handleMultiFilesChange = useCallback((files: File[]) => {
    setSelectedFiles(files);
    // Clear single file selections when using multi-upload
    if (files.length > 0) {
      // Clean up video preview URL
      if (videoPreview && videoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreview);
      }
      
      setSelectedImage(null);
      setSelectedVideo(null);
      setSelectedCover(null);
      setImagePreview(null);
      setVideoPreview(null);
      setCoverPreview(null);
    }
  }, [videoPreview]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Layout
      title={t('createPost.pageTitle')}
      description={t('createPost.pageDescription')}
      noIndex
    >
      <ErrorToast
        message={errorMessage}
        isVisible={showError}
        onClose={() => setShowError(false)}
      />
      <SuccessToast
        message={t('createPost.successMessage')}
        isVisible={showSuccess}
        onClose={() => setShowSuccess(false)}
      />

      {showVideoTrimmer && selectedVideo && (
        <VideoTrimEditor
          videoFile={selectedVideo}
          onSave={handleVideoTrimmerSave}
          onCancel={handleVideoTrimmerCancel}
        />
      )}
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className=""
      >
        <div className="bg-white dark:bg-gray-900 rounded-lg">
          <form onSubmit={handleSubmit} className="p-4 space-y-6">
            {/* Post Type Selector */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('createPost.postTypeLabel')}
              </label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setPostType('text');
                    setPollData(null);
                    clearMedia();
                  }}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    postType === 'text'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="text-center">
                    <svg className="w-6 h-6 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm font-medium">{t('createPost.tweet')}</span>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setPostType('regular');
                    setPollData(null);
                  }}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    postType === 'regular'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="text-center">
                    <svg className="w-6 h-6 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium">{t('createPost.regularPost')}</span>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setPostType('poll');
                    clearMedia();
                  }}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    postType === 'poll'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="text-center">
                    <svg className="w-6 h-6 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span className="text-sm font-medium">{t('createPost.poll')}</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Caption Input - hidden for poll (poll does not use caption) */}
            {postType !== 'poll' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('createPost.postText')} {postType === 'regular' && <span className="text-gray-500 dark:text-gray-400 text-xs">{t('createPost.optional')}</span>}
                  {postType === 'text' && <span className="text-gray-500 dark:text-gray-400 text-xs">{t('createPost.required')}</span>}
                </label>
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {showPreview ? t('createPost.hidePreview') : t('createPost.showPreview')}
                </button>
              </div>
              
              <div className={`grid gap-4 ${showPreview ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {}
                    }}
                    placeholder={postType === 'regular' ? t('createPost.captionPlaceholderOptional') : t('createPost.captionPlaceholder')}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                    rows={6}
                    maxLength={1000}
                    required={postType === 'text'}
                  />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{getTextLength(caption)}/1000</span>
                  </div>
                </div>
                
                {showPreview && (
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('createPost.preview')}</div>
                    <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 min-h-[144px]">
                      <TextPreview 
                        text={caption} 
                        className="text-gray-900 dark:text-gray-100 text-sm leading-relaxed"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Poll Creator */}
            {postType === 'poll' && (
              <PollCreator
                onPollChange={setPollData}
                disabled={uploadState.isUploading}
              />
            )}

            {/* Media Upload */}
            {postType === 'regular' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('createPost.mediaLabel')}
                </label>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <button
                    type="button"
                    onClick={() => {
                      setUseMultiUpload(!useMultiUpload);
                      clearMedia();
                    }}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      useMultiUpload
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {useMultiUpload ? t('createPost.multiUpload') : t('createPost.singleUpload')}
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('createPost.maxFileSize', { size: useMultiUpload ? '50' : '20' })}
                  </p>
                </div>
              </div>
              
              {/* Media Preview */}
              {(imagePreview || videoPreview) && (
                <div className="relative">
                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full max-h-96 object-cover rounded-lg"
                    />
                  )}
                  {videoPreview && (
                    <video
                      src={videoPreview}
                      controls
                      className="w-full max-h-96 object-cover rounded-lg"
                    />
                  )}
                  <button
                    type="button"
                    onClick={clearMedia}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Cover Image for Video */}
              {selectedVideo && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('createPost.coverLabel')}
                  </label>
                  {coverPreview ? (
                    <div className="relative">
                      <img
                        src={coverPreview}
                        alt="Cover Preview"
                        className="w-full max-h-48 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCover(null);
                          setCoverPreview(null);
                          if (coverInputRef.current) coverInputRef.current.value = '';
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => coverInputRef.current?.click()}
                      className="w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                    >
                      <div className="text-center">
                        <svg className="mx-auto h-8 w-8 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('createPost.selectCover')}</p>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {/* Multi-file Upload Component */}
              {useMultiUpload ? (
                <MultiFileUpload
                  onFilesChange={handleMultiFilesChange}
                  maxFiles={10}
                  maxFileSize={50}
                  disabled={uploadState.isUploading}
                />
              ) : (
                <>
                  {/* Upload Buttons */}
                  {!imagePreview && !videoPreview && (
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        className="flex-1 p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                      >
                        <div className="text-center">
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('createPost.selectImage')}</p>
                        </div>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => videoInputRef.current?.click()}
                        className="flex-1 p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                      >
                        <div className="text-center">
                          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('createPost.selectVideo')}</p>
                        </div>
                      </button>
                    </div>
                  )}
                </>
              )}

              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoSelect}
                className="hidden"
              />
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverSelect}
                className="hidden"
              />
            </div>
            )}

            {/* Tags: single input with suggestions + multi-select chips */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('createPost.tagsLabel')}
              </label>
              <div ref={tagContainerRef} className="relative">
                <div
                  className="min-h-[42px] flex flex-wrap items-center gap-2 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-shadow"
                  onClick={() => tagInputRef.current?.focus()}
                >
                  {selectedTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-sm rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-700"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleTagToggle(tag); }}
                        className="p-0.5 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        aria-label={t('createPost.removeTag')}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  <input
                    ref={tagInputRef}
                    type="text"
                    value={tagInput}
                    onChange={(e) => {
                      setTagInput(e.target.value);
                      setTagDropdownOpen(true);
                    }}
                    onFocus={() => setTagDropdownOpen(true)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder={selectedTags.length === 0 ? t('createPost.tagInputPlaceholder') : ''}
                    className="flex-1 min-w-[120px] py-1 px-0 bg-transparent border-0 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-0 focus:outline-none text-sm"
                  />
                </div>

                {/* Dropdown: existing matches + create option */}
                {tagDropdownOpen && (
                  <div className="absolute z-10 left-0 right-0 mt-1 py-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto">
                    {(() => {
                      const q = tagInput.trim().toLowerCase();
                      const matching = allTags.filter(
                        (tag: any) =>
                          tag.name.toLowerCase().includes(q) &&
                          !selectedTags.includes(normalizeTagName(tag.name))
                      );
                      const exactMatch = q && allTags.some((tag: any) => normalizeTagName(tag.name) === q);
                      const canCreate = q.length > 0 && !exactMatch && !selectedTags.includes(q);
                      const hasSuggestions = matching.length > 0 || canCreate;
                      if (!hasSuggestions && !q) {
                        return (
                          <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            {t('createPost.typeToSearchOrCreate')}
                          </p>
                        );
                      }
                      if (!hasSuggestions) {
                        return (
                          <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            {t('createPost.noTagsFound')}
                          </p>
                        );
                      }
                      return (
                        <>
                          {matching.slice(0, 8).map((tag: any) => (
                            <button
                              key={tag.id || tag.name}
                              type="button"
                              onClick={() => selectExistingTag(tag.name)}
                              className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <span className="text-gray-400">#</span>
                              {tag.name}
                            </button>
                          ))}
                          {canCreate && (
                            <button
                              type="button"
                              onClick={() => addTagByName(q)}
                              disabled={createTagMutation.isPending}
                              className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-2 disabled:opacity-50"
                            >
                              <span className="text-blue-500">+</span>
                              {t('createPost.createTag')} &quot;#{q}&quot;
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={
                  createPostMutation.isPending ||
                  uploadState.isUploading ||
                  (postType === 'text' && !caption.trim()) ||
                  (postType === 'poll' && (!pollData || !pollData.question?.trim() || (pollData.options?.length ?? 0) < 2))
                }
                className="flex-1 py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploadState.isUploading ? t('createPost.uploadingProgress', { progress: Math.round(uploadState.progress) }) :
                 createPostMutation.isPending ? t('createPost.sending') : t('createPost.publishPost')}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </Layout>
  );
};

export default CreatePost;